import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuotes, useDeleteQuote } from '@/hooks/useQuote';
import { useToast } from '@/contexts/ToastContext';
import { ListSkeleton } from '@/components/ui/SkeletonLoader';
import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  processing: { bg: '#fef3c7', text: '#92400e' },
  ready: { bg: 'var(--color-success-50)', text: 'var(--color-success-600)' },
  review: { bg: '#ede9fe', text: '#5b21b6' },
  expired: { bg: 'var(--bg-surface-sunken)', text: 'var(--text-tertiary)' },
  ordered: { bg: 'var(--color-primary-50)', text: 'var(--color-primary-700)' },
};

const DEFAULT_STATUS = { bg: 'var(--bg-surface-sunken)', text: 'var(--text-tertiary)' };

export const QuotesListPage = () => {
  const { data, isLoading, error } = useQuotes();
  const deleteQuote = useDeleteQuote();
  const toast = useToast();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, quoteId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(quoteId);
  };

  const handleConfirmDelete = () => {
    if (!confirmDeleteId) return;
    deleteQuote.mutate(confirmDeleteId, {
      onSuccess: () => {
        setConfirmDeleteId(null);
        toast.success('Quote deleted');
      },
      onError: () => toast.error('Failed to delete quote'),
    });
  };

  return (
    <div
      className="container-main"
      style={{
        maxWidth: '896px',
        paddingTop: 'var(--space-12)',
        paddingBottom: 'var(--space-12)',
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 'var(--space-8)' }}
      >
        <h1
          style={{
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          My Quotes
        </h1>
        <Link to="/quote">
          <Button>New Quote</Button>
        </Link>
      </div>

      {isLoading && <ListSkeleton />}

      {error && (
        <div
          className="text-center"
          style={{
            padding: 'var(--space-8)',
            color: 'var(--color-error-500)',
            fontSize: 'var(--text-sm)',
          }}
        >
          Failed to load quotes. Please try again.
        </div>
      )}

      {data && data.data.length === 0 && (
        <div
          className="text-center"
          style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
            paddingTop: 'var(--space-16)',
            paddingBottom: 'var(--space-16)',
          }}
        >
          <p style={{ color: 'var(--text-tertiary)' }}>No quotes yet.</p>
          <Link
            to="/quote"
            style={{
              marginTop: 'var(--space-2)',
              display: 'inline-block',
              color: 'var(--text-accent)',
              textDecoration: 'none',
            }}
          >
            Upload a 3D model to get started
          </Link>
        </div>
      )}

      {data && data.data.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {data.data.map((quote) => {
            const statusColor = STATUS_COLORS[quote.status] ?? DEFAULT_STATUS;
            const isHovered = hoveredId === quote.quoteId;
            const canDelete = quote.status !== 'ordered';

            return (
              <Link
                key={quote.quoteId}
                to={`/quote?id=${quote.quoteId}`}
                style={{
                  display: 'block',
                  position: 'relative',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-default)',
                  backgroundColor: 'var(--bg-surface)',
                  padding: 'var(--space-4)',
                  textDecoration: 'none',
                  transition: `box-shadow var(--duration-normal) var(--ease-out)`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  setHoveredId(quote.quoteId);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  setHoveredId(null);
                }}
              >
                {/* Delete button */}
                {canDelete && isHovered && (
                  <button
                    type="button"
                    onClick={(e) => handleDeleteClick(e, quote.quoteId)}
                    aria-label="Delete quote"
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: 'var(--text-tertiary)',
                      color: '#fff',
                      fontSize: '14px',
                      lineHeight: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'background-color 150ms ease',
                      zIndex: 1,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-error-500)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--text-tertiary)';
                    }}
                  >
                    {'\u00d7'}
                  </button>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center justify-between" style={{ gap: 'var(--space-2)' }}>
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {quote.fileName}
                    </p>
                    <p
                      style={{
                        marginTop: 'var(--space-1)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--text-tertiary)',
                      }}
                    >
                      {quote.volumeCm3 > 0 && `${quote.volumeCm3} cm3 | `}
                      {new Date(quote.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center" style={{ gap: 'var(--space-3)', flexShrink: 0 }}>
                    {quote.status === 'ready' && quote.prices.length > 0 && (
                      <span
                        style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-semibold)',
                          color: 'var(--text-primary)',
                        }}
                      >
                        from ${Math.min(...quote.prices.map((p) => p.price)).toFixed(2)}
                      </span>
                    )}
                    <span
                      style={{
                        borderRadius: 'var(--radius-full)',
                        padding: 'var(--space-1) var(--space-2)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        backgroundColor: statusColor.bg,
                        color: statusColor.text,
                      }}
                    >
                      {quote.status}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          title="Delete quote"
          message="Are you sure you want to delete this quote? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Keep"
          onConfirm={handleConfirmDelete}
          onClose={() => setConfirmDeleteId(null)}
          isPending={deleteQuote.isPending}
        />
      )}
    </div>
  );
};
